import React, { useState, useRef } from 'react';
import axios from 'axios';

/**
 * LogoUploader - Wiederverwendbare Komponente für Logo-Uploads
 *
 * Props:
 * - label: Beschriftung des Uploaders
 * - settingKey: Key der Setting in der Datenbank (z.B. 'logo_url')
 * - currentValue: Aktueller URL-Wert
 * - onUploadSuccess: Callback nach erfolgreichem Upload
 * - helpText: Optionaler Hilfetext
 */
function LogoUploader({
  label,
  settingKey,
  currentValue,
  onUploadSuccess,
  helpText = 'Unterstützte Formate: JPG, PNG, SVG, WebP (max. 5MB)'
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(currentValue || '');
  const fileInputRef = useRef(null);

  // Datei hochladen
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validierung
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Ungültiges Dateiformat. Erlaubt: JPG, PNG, SVG, WebP');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Datei zu groß. Maximum: 5MB');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Datei hochladen
      const uploadResponse = await axios.post('/api/upload-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const logoUrl = uploadResponse.data.url;

      // Setting aktualisieren
      await axios.put(
        `/api/system-settings/${settingKey}?value=${encodeURIComponent(logoUrl)}`,
        {}
      );

      // Preview aktualisieren
      setPreview(logoUrl);

      // Callback aufrufen
      if (onUploadSuccess) {
        onUploadSuccess(logoUrl);
      }

    } catch (err) {
      console.error('Upload-Fehler:', err);
      setError(err.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploading(false);
      // Input zurücksetzen
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Logo entfernen
  const handleRemove = async () => {
    try {

      await axios.put(
        `/api/system-settings/${settingKey}?value=`,
        {}
      );

      setPreview('');

      if (onUploadSuccess) {
        onUploadSuccess('');
      }

    } catch (err) {
      console.error('Fehler beim Entfernen:', err);
      setError('Fehler beim Entfernen des Logos');
    }
  };

  return (
    <div className="mb-6">
      <label className="block font-semibold text-lg mb-2 text-slate-800 dark:text-white">
        {label}
      </label>

      {helpText && (
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
          {helpText}
        </p>
      )}

      {/* Preview */}
      {preview && (
        <div className="relative inline-block p-4 bg-slate-50 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg mb-4">
          <img
            src={preview}
            alt={label}
            className="max-h-20 max-w-[200px] object-contain block"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <button
            type="button"
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white border-none text-base leading-none cursor-pointer flex items-center justify-center transition-colors hover:bg-red-600"
            onClick={handleRemove}
            title="Logo entfernen"
          >
            ×
          </button>
        </div>
      )}

      {/* Upload-Bereich */}
      <div className="flex gap-4 items-center flex-wrap">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
          onChange={handleFileUpload}
          disabled={uploading}
          className="p-2.5 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-white text-sm cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
        />

        {uploading && (
          <span className="text-slate-500 dark:text-slate-400 text-sm">
            Wird hochgeladen...
          </span>
        )}
      </div>

      {/* Fehleranzeige */}
      {error && (
        <div className="mt-2 px-3 py-2 bg-red-500/10 border border-red-500 rounded-md text-red-500 text-sm">
          {error}
        </div>
      )}
    </div>
  );
}

export default LogoUploader;
